define(['underscore'],
    (_)=> {
        /**
         * Returns a constructor object for creating new View Model Node objects.
         * A view model node is a pointer to an underlying model object. It also contains aspect properties
         * based on the underlying model value and metadata
         *
         * @param modelRoot {Object}: The root model object that was used to create the view model
         * @param aspects {Array}: Aspect factory objects that have been registered with the ViewModel API
         *
         * @return {Function}: A constructor function to create new View Model Node objects
         **/
        return function (modelRoot, aspects) {
            function VMNode(metadataInfo, ancestorChain, accessorCode) {
                this._ancestorChain = ancestorChain;
                this._metadataInfo = metadataInfo;
                this._accessorCode = accessorCode;
                this._aspectFactories = aspects;
                this._aspects = {};

                /* Provide more convenient methods for the typelists. They provide an automatic conversion between
                 * typecodes and string values on the model.
                 */
                if (metadataInfo.valueType.kind === 'class' && metadataInfo.valueType.typeInfo.metaType.isTypelist) {
                    this.get = typelistGet;
                    this.set = typelistSet;
                }

                /*mixin aspect properties to the view model node */
                aspects.forEach(function (aspectFactory) {
                    const aspectProperties = aspectFactory.getAspectProperties(this, this._metadataInfo, this._ancestorChain);
                    if (aspectProperties) {
                        Object.getOwnPropertyNames(aspectProperties).forEach(function (propName) {
                            if (this._aspects.hasOwnProperty(propName)) {
                                throw new Error(`View model node already has a property ${propName}, it cannot be overridden by another aspect`);
                            }
                            Object.defineProperty(this._aspects, propName, aspectProperties[propName]);
                        }, this);
                    }
                }, this);
            }

            /**
             * Typelist version of the get function. It provides automatic conversion into the typecode.
             */
            function typelistGet() {
                const baseValue = VMNode.prototype.get.call(this);
                if (_.isString(baseValue)) {
                    return _.findWhere(this._metadataInfo.valueType.typeInfo.codes, {code: baseValue});
                }
                return baseValue;
            }


            /** Typelist version of the setter. */
            function typelistSet(vmNodeOrModel) {
                if (vmNodeOrModel instanceof VMNode) {
                    vmNodeOrModel = vmNodeOrModel.value;
                }

                if (vmNodeOrModel && !_.isString(vmNodeOrModel)) {
                    /* Somebody (like an angular) cheats and provides clones of the objects. Do not allow that! */
                    const trueEntity = this._metadataInfo.valueType.typeInfo.getCode(vmNodeOrModel.code);
                    if (trueEntity !== vmNodeOrModel) {
                        console.error('Critical MVC violation: attempt to provide non-genuine typecode');
                        vmNodeOrModel = trueEntity;
                    }
                    vmNodeOrModel = vmNodeOrModel.code;
                }

                VMNode.prototype.set.call(this, vmNodeOrModel);
            }


            VMNode.prototype = {
                get() {
                    if (this._ancestorChain.parent) {
                        return this._ancestorChain.parent.value[this._propertyName];
                    }
                    return modelRoot;
                },

                set(vmNodeOrModel) {
                    if (vmNodeOrModel instanceof VMNode) {
                        vmNodeOrModel = vmNodeOrModel.value;
                    }
                    if (!this._ancestorChain.parent) {
                        modelRoot = vmNodeOrModel;
                    } else {
                        this._ancestorChain.parent.value[this._propertyName] = vmNodeOrModel;
                    }
                },

                get value() {
                    return this.get();
                },
                set value(pathOrValue) {
                    this.set(pathOrValue);
                },

                get aspects() {
                    return this._aspects;
                },

                get _propertyName() {
                    return this._accessorCode;
                },

                get _parent() {
                    return this._ancestorChain.parent;
                }
            };

            return VMNode;
        };
    });


/** WEBPACK FOOTER **
 ** ./common/js/src/edge/platform/viewmodel/_VMNode.js
 **/